import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = React.useState<{ totalUsers: number; totalServers: number; totalComments: number } | null>(null);

  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await import('../utils/api').then(m => m.adminAPI.getStats());
        setStats(data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    if (user?.role === 'admin') {
      fetchStats();
    }
  }, [user]);

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <div className="container mx-auto py-16 px-4 text-center">
        <div className="bg-red-100 dark:bg-red-900/60 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-100 rounded-xl p-8 max-w-2xl mx-auto">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-2xl font-bold mb-2">{t('admin.access_denied')}</h2>
          <p className="text-lg">{t('admin.access_denied_desc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <h1 className="text-4xl font-bold mb-8 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
        {t('admin.dashboard')}
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Server Management Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors shadow-md dark:shadow-none">
          <div className="flex items-center mb-4">
            <svg className="w-8 h-8 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('admin.servers')}</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{t('admin.manage_servers_desc')}</p>
          <button
            onClick={() => navigate('/admin/servers')}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('admin.manage_servers')}
          </button>
        </div>

        {/* Ad Management Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:border-pink-500 dark:hover:border-pink-500 transition-colors shadow-md dark:shadow-none">
          <div className="flex items-center mb-4">
            <svg className="w-8 h-8 text-pink-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('admin.manage_ads')}</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{t('admin.manage_ads_desc')}</p>
          <button
            onClick={() => navigate('/admin/ads')}
            className="w-full bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 rounded-lg transition-all shadow-md hover:shadow-lg"
          >
            {t('admin.manage_ads')}
          </button>
        </div>

        {/* User Management Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:border-purple-500 dark:hover:border-purple-500 transition-colors shadow-md dark:shadow-none">
          <div className="flex items-center mb-4">
            <svg className="w-8 h-8 text-purple-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('admin.users')}</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{t('admin.manage_users_desc')}</p>
          <button
            onClick={() => navigate('/admin/users')}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-lg transition-all shadow-md hover:shadow-lg"
          >
            {t('admin.manage_users')}
          </button>
        </div>

        {/* Comments & Reviews Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:border-green-500 dark:hover:border-green-500 transition-colors shadow-md dark:shadow-none">
          <div className="flex items-center mb-4">
            <svg className="w-8 h-8 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('admin.comments')}</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{t('admin.moderate_comments_desc')}</p>
          <button
            onClick={() => navigate('/admin/comments')}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition-all shadow-md hover:shadow-lg"
          >
            {t('admin.moderate_comments')}
          </button>
        </div>

        {/* Statistics Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:border-yellow-500 dark:hover:border-yellow-500 transition-colors shadow-md dark:shadow-none">
          <div className="flex items-center mb-4">
            <svg className="w-8 h-8 text-yellow-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('admin.stats')}</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{t('admin.view_stats_desc')}</p>
          <button
            onClick={() => navigate('/admin/statistics')}
            className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-3 rounded-lg transition-all shadow-md hover:shadow-lg"
          >
            {t('admin.view_stats')}
          </button>
        </div>

        {/* Settings Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:border-red-500 dark:hover:border-red-500 transition-colors shadow-md dark:shadow-none">
          <div className="flex items-center mb-4">
            <svg className="w-8 h-8 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('admin.settings')}</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{t('admin.system_settings_desc')}</p>
          <button
            onClick={() => navigate('/admin/settings')}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition-all shadow-md hover:shadow-lg"
          >
            {t('admin.system_settings')}
          </button>
        </div>

        {/* Logs Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:border-indigo-500 dark:hover:border-indigo-500 transition-colors shadow-md dark:shadow-none">
          <div className="flex items-center mb-4">
            <svg className="w-8 h-8 text-indigo-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('admin.logs')}</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{t('admin.view_logs_desc')}</p>
          <button
            onClick={() => navigate('/admin/logs')}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-lg transition-all shadow-md hover:shadow-lg"
          >
            {t('admin.view_logs')}
          </button>
        </div>
      </div>

      {/* Quick Stats Section */}
      <div className="mt-8 bg-white dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-md dark:shadow-none">
        <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">{t('admin.quick_overview')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats?.totalServers || 0}</div>
            <div className="text-gray-600 dark:text-gray-400 text-sm mt-1">{t('admin.stats.total_servers')}</div>
          </div>
          <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{stats?.totalUsers || 0}</div>
            <div className="text-gray-600 dark:text-gray-400 text-sm mt-1">{t('admin.stats.total_users')}</div>
          </div>
          <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">{stats?.totalComments || 0}</div>
            <div className="text-gray-600 dark:text-gray-400 text-sm mt-1">{t('admin.stats.total_comments')}</div>
          </div>
          <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{stats?.totalServers || 0}</div>
            <div className="text-gray-600 dark:text-gray-400 text-sm mt-1">{t('admin.stats.active_servers')}</div>
          </div>
        </div>
      </div>
    </div >
  );
};

export default AdminPanel;

